import { useWikiSpacePages } from "@/hooks/use-wiki-space-pages";
import { Divider } from "@msuite/ui/divider";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Tag } from "@msuite/ui/tag";
import { guardString } from "@msuite/utils/strings";
import React from "react";
import { useNavigate } from "react-router";
import { findPageLineage } from "./wiki-page-lineage";

/** Props Interface */
interface WikiPageSubPagesProps {
	spaceId: string;
	pageId: string;
}

/** The page's children as a row of chips at the end of the document; nothing when it has none. */
export function WikiPageSubPages({ spaceId, pageId }: WikiPageSubPagesProps) {
	/** Hooks */
	const { t } = useTranslation();
	const navigate = useNavigate();

	/** Query */
	const { wikiSpacePages } = useWikiSpacePages(spaceId);

	/** Computed */
	const { page } = findPageLineage(wikiSpacePages, pageId);
	const children = page?._children ?? [];

	/** Guard */
	if (children.length === 0) return null;

	/** Render */
	return (
		<React.Fragment>
			<Divider className="mt-10 mb-5" />
			<section className="v-stack">
				<p className="label">{t("wiki.sub_pages")}</p>
				<div className="flex flex-row flex-wrap gap-2">
					{children.map((child) => (
						<Tag
							key={child.id}
							color="gray"
							withStatusIndicator={false}
							onClick={() => navigate(`/wiki/${spaceId}/${child.id}`)}
						>
							<span className="h-stack gap-1.5">
								<Icon
									name="FileText"
									size={12}
									className="shrink-0 opacity-50"
								/>
								{guardString(child.title, t("wiki.untitled"))}
							</span>
						</Tag>
					))}
				</div>
			</section>
		</React.Fragment>
	);
}
